import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { ArrowRight } from "react-feather";
import axios from "axios";
import { getBrandsSubscriptionPlans } from "@src/redux/actions/brandProfile/index";
import { getDashboardGuidance } from "@src/redux/actions/brandsDashboard";

const getToken = () => localStorage.getItem(process.env.REACT_APP_USERTOKEN);
const API_URL = process.env.REACT_APP_BASEURL || "http://localhost:8000/api/";

export const formatDateOnly = (rawDate) => {
  if (!rawDate) return "";
  try {
    const d = new Date(rawDate);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    }
    return String(rawDate).replace(/\s+\d{1,2}:\d{2}(:\d{2})?\s*(AM|PM)?$/i, "").trim();
  } catch (e) {
    return String(rawDate);
  }
};

export const formatPlanDisplay = (planName, subscriptionType) => {
  if (!planName) return null;
  let name = String(planName).trim();
  if (!name) return null;

  let intervalRaw = String(subscriptionType || "").toLowerCase();
  if (!intervalRaw) {
    if (/annual|yearly|year/i.test(name)) {
      intervalRaw = "annual";
    } else if (/quarter/i.test(name)) {
      intervalRaw = "quarterly";
    } else if (/month/i.test(name)) {
      intervalRaw = "monthly";
    }
  }

  const cleanBaseName = name
    .replace(/\b(annual|quarterly|monthly|yearly)\b/gi, "")
    .replace(/\b(plan|membership)\b/gi, "")
    .trim();

  const finalName = cleanBaseName || name.replace(/\bplan\b/gi, "").trim() || "Membership";

  let interval = "Quarterly plan";
  if (intervalRaw.includes("annual") || intervalRaw.includes("year")) {
    interval = "Annual plan";
  } else if (intervalRaw.includes("month")) {
    interval = "Monthly plan";
  } else if (intervalRaw.includes("quarter")) {
    interval = "Quarterly plan";
  } else if (!intervalRaw) {
    return finalName;
  }

  return `${finalName} · ${interval}`;
};

export const getScheduledRenewalPlanName = (renewal, currentPlanData) => {
  const plan = currentPlanData?.data || currentPlanData || {};
  const currentPlanName =
    renewal?.plan_name ||
    plan?.subscription_name ||
    plan?.plan ||
    "Membership";

  const scheduled =
    renewal?.scheduled_subscription ||
    renewal?.upcoming_subscription ||
    plan?.upcoming_subscription ||
    plan?.scheduled_subscription ||
    currentPlanData?.data?.upcoming_subscription ||
    currentPlanData?.upcoming_subscription;

  if (scheduled) {
    return (
      scheduled.plan_name ||
      scheduled.subscription_name ||
      scheduled.name ||
      scheduled.description ||
      currentPlanName
    );
  }
  return currentPlanName;
};

export const getScheduledRenewalStartDate = (renewal, currentPlanData) => {
  const plan = currentPlanData?.data || currentPlanData || {};
  const scheduled =
    renewal?.scheduled_subscription ||
    renewal?.upcoming_subscription ||
    plan?.upcoming_subscription ||
    plan?.scheduled_subscription ||
    currentPlanData?.data?.upcoming_subscription ||
    currentPlanData?.upcoming_subscription;

  const rawEndDate =
    renewal?.period_end_at ||
    renewal?.renewal_date ||
    plan?.expiry_date ||
    plan?.subscription_end_date;

  if (scheduled) {
    return (
      scheduled.starts_at ||
      scheduled.period_start_at ||
      scheduled.start_date ||
      scheduled.startDate ||
      rawEndDate
    );
  }
  return rawEndDate;
};

export const formatRenewalDate = (rawDate, isCancelled = false, isSuspended = false, isInRetryGrace = false) => {
  if (isSuspended) return "Account suspended";
  if (isInRetryGrace) return "Payment retry in progress";
  if (!rawDate) return null;
  const formatted = formatDateOnly(rawDate);
  if (!formatted) return null;
  return isCancelled ? `Access ends ${formatted}` : `Renews ${formatted}`;
};

/**
 * Resolves the smart button configuration (label and navigation target)
 * based on brand's subscription, renewal, and invoice status.
 */
export const getSmartSubscriptionButtonConfig = ({
  plan = {},
  renewal = {},
  invoiceData = null,
  unpaidInvoice = null
}) => {
  // Check upcoming / scheduled subscription
  const scheduledSub =
    plan?.upcoming_subscription ||
    plan?.scheduled_subscription ||
    plan?.upcoming ||
    renewal?.scheduled_subscription ||
    renewal?.upcoming_subscription ||
    invoiceData?.upcoming_subscription ||
    null;
  const hasScheduledRenewal = Boolean(scheduledSub);

  // Status flags
  const isSuspended = Boolean(plan?.is_suspended || renewal?.status === "suspended");
  const isInRetryGrace = Boolean(
    plan?.is_in_retry_grace ||
    plan?.billing_status === "past_due" ||
    (unpaidInvoice && unpaidInvoice?.status === "payment_failed")
  );
  const isCancelled = Boolean(plan?.is_cancelled || plan?.billing_status === "cancelled");
  const isExpired = Boolean(
    plan?.is_expire ||
    plan?.subscription_exp ||
    plan?.status === false ||
    (invoiceData && invoiceData.is_subscribed === false && !invoiceData.has_previous_plan)
  );

  // 1. Renewal already paid and scheduled -> View Scheduled Renewal (prevents duplicate payment requests)
  if (hasScheduledRenewal) {
    return {
      actionText: "View Scheduled Renewal",
      label: "View Scheduled Renewal",
      to: "/dashboard/brands/subscription-plan",
      actionType: "view_scheduled_renewal"
    };
  }

  // 2. Suspended account -> Resolve Billing
  if (isSuspended) {
    return {
      actionText: "Resolve Billing",
      label: "Resolve Billing",
      to: "/dashboard/brands/subscription-plan",
      actionType: "resolve_billing"
    };
  }

  // 3. Payment failed / In retry grace -> Update Payment
  if (isInRetryGrace) {
    return {
      actionText: "Update Payment",
      label: "Update Payment",
      to: "/dashboard/brands/subscription-plan",
      actionType: "update_payment"
    };
  }

  // 4. Subscription expired -> Renew Subscription
  if (isExpired) {
    return {
      actionText: "Renew Subscription",
      label: "Renew Subscription",
      to: "/subscription-plans",
      actionType: "renew_expired"
    };
  }

  // 5. Subscription cancelled (non-renewing, access ending at period end) -> Renew Subscription
  if (isCancelled) {
    return {
      actionText: "Renew Subscription",
      label: "Renew Subscription",
      to: "/dashboard/brands/subscription-plan",
      actionType: "renew_cancelled"
    };
  }

  // Check unpaid invoice
  const resolvedUnpaidInvoice =
    unpaidInvoice ||
    invoiceData?.unpaid_invoice ||
    (renewal?.invoice && renewal?.invoice?.status !== "paid" ? renewal.invoice : null) ||
    (plan?.hosted_invoice_url && !isExpired ? { hosted_invoice_url: plan.hosted_invoice_url } : null);

  const hasUnpaidInvoice = Boolean(resolvedUnpaidInvoice);

  // 6. Renewal invoice already generated but unpaid -> Renew Subscription (opens invoice/payment flow)
  if (hasUnpaidInvoice) {
    return {
      actionText: "Renew Subscription",
      label: "Renew Subscription",
      to: "/dashboard/brands/subscription-plan?action=view_invoice",
      actionType: "pay_invoice",
      hostedInvoiceUrl: resolvedUnpaidInvoice?.hosted_invoice_url || null
    };
  }

  // Check invoice billing preference and days until renewal
  const isInvoiceUser = Boolean(
    plan?.invoice_renewal ||
    renewal?.billing_mode === "invoice" ||
    plan?.subscription_type === "invoice"
  );

  const daysUntilRenewal =
    renewal?.days_until_renewal ??
    plan?.days_until_renewal ??
    null;

  const isCloseToExpiry = daysUntilRenewal !== null && daysUntilRenewal <= 30;

  // 7. Subscription close to expiry, invoice payment user, no renewal invoice -> Generate New Invoice
  if (isInvoiceUser && isCloseToExpiry) {
    return {
      actionText: "Generate New Invoice",
      label: "Generate New Invoice",
      to: "/dashboard/brands/subscription-plan?action=request_invoice",
      actionType: "generate_invoice"
    };
  }

  // 8. Subscription active with enough time remaining -> Manage Subscription
  return {
    actionText: "Manage Subscription",
    label: "Manage Subscription",
    to: "/dashboard/brands/subscription-plan",
    actionType: "manage_subscription"
  };
};

const BrandSidebarPlanFooter = ({ menuCollapsed, menuHover }) => {
  const dispatch = useDispatch();
  const [invoiceData, setInvoiceData] = useState(null);

  const currentPlanData = useSelector(
    (state) => state.brandDashboard?.currentPlanData
  );
  const dashboardGuidanceData = useSelector(
    (state) => state.brandsDashboardSlice?.dashboardGuidanceData
  );

  useEffect(() => {
    if (!currentPlanData) {
      dispatch(getBrandsSubscriptionPlans());
    }
    if (!dashboardGuidanceData) {
      dispatch(getDashboardGuidance());
    }
  }, [currentPlanData, dashboardGuidanceData, dispatch]);

  useEffect(() => {
    const token = getToken();
    if (!token) return;

    axios
      .get(`${API_URL}brand/subscription-invoices`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`
        }
      })
      .then((res) => {
        if (res.data?.status) {
          setInvoiceData(res.data);
        }
      })
      .catch(() => {});
  }, [currentPlanData, dashboardGuidanceData]);

  const plan = currentPlanData?.data || currentPlanData || {};
  const renewal = dashboardGuidanceData?.renewal || {};

  // Check if API response indicates no subscription or expired
  const hasNoSubscription =
    currentPlanData?.status === false ||
    plan?.status === false ||
    plan?.subscription_exp === true;

  const isExpired = Boolean(plan?.is_expire);
  const isCancelled = Boolean(plan?.is_cancelled);
  const isSuspended = Boolean(plan?.is_suspended);
  const isInRetryGrace = Boolean(plan?.is_in_retry_grace);

  // Discover upcoming/scheduled subscription across all response shapes
  const upcomingSub =
    plan?.upcoming_subscription ||
    plan?.scheduled_subscription ||
    plan?.upcoming ||
    currentPlanData?.data?.upcoming_subscription ||
    currentPlanData?.data?.scheduled_subscription ||
    currentPlanData?.data?.upcoming ||
    currentPlanData?.upcoming_subscription ||
    currentPlanData?.scheduled_subscription ||
    currentPlanData?.upcoming ||
    renewal?.scheduled_subscription ||
    renewal?.upcoming_subscription ||
    renewal?.upcoming ||
    dashboardGuidanceData?.renewal?.scheduled_subscription ||
    dashboardGuidanceData?.scheduled_subscription ||
    dashboardGuidanceData?.upcoming_subscription ||
    invoiceData?.upcoming_subscription ||
    null;

  const hasScheduledRenewal = Boolean(upcomingSub);

  // Real plan name & interval from API
  const rawPlanName =
    plan?.plan ||
    plan?.subscription_name ||
    plan?.description ||
    renewal?.plan_name ||
    "";

  const rawInterval =
    plan?.subscription_type ||
    plan?.duration ||
    renewal?.interval ||
    "";

  // Real date from API
  const rawDate =
    plan?.expiry_date ||
    plan?.period_end_at ||
    plan?.raw_expiry_date ||
    plan?.subscription_end_date ||
    renewal?.period_end_at ||
    renewal?.renewal_date ||
    null;

  const isVisible = !menuCollapsed || menuHover;
  if (!isVisible) return null;

  // If data hasn't arrived from API yet, don't show any dummy data
  if (!currentPlanData && !dashboardGuidanceData) {
    return null;
  }

  // Calculate dynamic button config based on actual subscription and invoice state
  const buttonConfig = getSmartSubscriptionButtonConfig({
    plan,
    renewal,
    invoiceData,
    unpaidInvoice: invoiceData?.unpaid_invoice
  });

  // Handle expired or no subscription state
  if (hasNoSubscription || isExpired) {
    const expiredDateText = rawDate ? `Expired on ${formatDateOnly(rawDate)}` : "No active subscription";
    return (
      <div className="brand-sidebar-plan-footer" id="brand-sidebar-plan-footer">
        <div className="plan-title">Subscription Expired</div>
        <div className="plan-renews">{expiredDateText}</div>
        <Link
          to={buttonConfig.to}
          className="sidebar-plan-action-link"
          id="sidebar-brand-switch-annual"
        >
          <span>{buttonConfig.actionText}</span>
          <ArrowRight size={13} className="ml-25" />
        </Link>
      </div>
    );
  }

  // Scheduled renewals show upcoming plan name instead of current plan
  const activePlanName = hasScheduledRenewal
    ? (upcomingSub?.plan_name || upcomingSub?.subscription_name || upcomingSub?.name || upcomingSub?.description || upcomingSub?.plan || rawPlanName)
    : rawPlanName;

  const activeInterval = hasScheduledRenewal
    ? (upcomingSub?.subscription_type || upcomingSub?.duration || upcomingSub?.interval || rawInterval)
    : rawInterval;

  // Scheduled renewals use starts_at start-date field
  const activeDate = hasScheduledRenewal
    ? (upcomingSub?.starts_at || upcomingSub?.period_start_at || upcomingSub?.start_date || upcomingSub?.startDate || rawDate)
    : rawDate;

  // If no plan name could be found at all from real data, avoid showing fake plan
  if (!activePlanName && !activeDate) {
    return null;
  }

  const planTitle = formatPlanDisplay(activePlanName, activeInterval) || activePlanName || "Active Plan";
  const renewalText = formatRenewalDate(activeDate, isCancelled, isSuspended, isInRetryGrace);

  return (
    <div className="brand-sidebar-plan-footer" id="brand-sidebar-plan-footer">
      <div className="plan-title">{planTitle}</div>
      {renewalText && <div className="plan-renews">{renewalText}</div>}
      <Link
        to={buttonConfig.to}
        className="sidebar-plan-action-link"
        id="sidebar-brand-switch-annual"
      >
        <span>{buttonConfig.actionText}</span>
        <ArrowRight size={13} className="ml-25" />
      </Link>
    </div>
  );
};

export default BrandSidebarPlanFooter;

